/**
 * Alpine.js data context for the video filter bar on /yoga-videos.
 *
 * Handles:
 * - 7 filter dimensions: Lehrer, Level, Dauer, Thema, Yoga-Stil, Anstrengung, Praxis
 * - NL search pre-population (chips and sliders jump to NL-interpreted values)
 * - Additive manual filter selection via dropdowns and chips
 * - "Alle löschen" resets all filters + clears NL search state
 * - Video count display (totalCount from AJAX response)
 * - Window-slider for Dauer (10-min window, 5-min steps) and Anstrengung (3-point window, 1-point steps)
 * - Submits to filterAction via the existing legacy AJAX infrastructure
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NlFilterChip {
  uid: number;
  label: string;
}

interface NlFilters {
  teachers: NlFilterChip[];
  styles: NlFilterChip[];
  topics: NlFilterChip[];
  levelMin: number | null;
  levelMax: number | null;
  durationMin: number | null;
  durationMax: number | null;
  sweatMin: number | null; // UI scale 1–10
  sweatMax: number | null; // UI scale 1–10
  category: number | false;
  /** Music preference: '1' = with music, '2' = without, null = no preference. */
  music?: "1" | "2" | null;
  titleHints?: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Duration slider: window width in minutes */
const DUR_WINDOW = 10;
/** Duration slider: step size in minutes */
const DUR_STEP = 5;
/**
 * Visible max on the duration axis. Videos longer than this land on the
 * "120+" tick at the right edge.
 */
const DUR_UI_MAX = 120;
/**
 * Non-linear axis: videos under 60 min are the vast majority, so the 0–60
 * segment gets more horizontal room. Breakpoint minute and its % position.
 */
const DUR_BREAK_MIN = 60;
// 60 min sits at 60% of the track – gives the 0–60 segment more horizontal
// room (where most videos are) without squishing the 60–120 segment too much.
const DUR_BREAK_PCT = 0.6;
// 120 min sits at 95% – the remaining 5% is the "120+" open-ended bucket.
const DUR_MAX_PCT = 0.95;

/** Map a minute value (0..DUR_UI_MAX) to its horizontal position on the axis (0..1). */
function durMinToPct(min: number): number {
  if (min <= 0) return 0;
  if (min <= DUR_BREAK_MIN) {
    return (min / DUR_BREAK_MIN) * DUR_BREAK_PCT;
  }
  if (min <= DUR_UI_MAX) {
    return (
      DUR_BREAK_PCT +
      ((min - DUR_BREAK_MIN) / (DUR_UI_MAX - DUR_BREAK_MIN)) *
        (DUR_MAX_PCT - DUR_BREAK_PCT)
    );
  }
  return 1;
}

/** Inverse of durMinToPct: map a 0..1 position back to minutes. */
function durPctToMin(pct: number): number {
  if (pct <= 0) return 0;
  if (pct >= 1) return DUR_UI_MAX;
  if (pct <= DUR_BREAK_PCT) {
    return (pct / DUR_BREAK_PCT) * DUR_BREAK_MIN;
  }
  if (pct <= DUR_MAX_PCT) {
    return (
      DUR_BREAK_MIN +
      ((pct - DUR_BREAK_PCT) / (DUR_MAX_PCT - DUR_BREAK_PCT)) *
        (DUR_UI_MAX - DUR_BREAK_MIN)
    );
  }
  return DUR_UI_MAX;
}

/** Reads the horizontal pointer position from either a mouse or touch event. */
function getClientX(e: MouseEvent | TouchEvent): number {
  return "touches" in e ? e.touches[0].clientX : e.clientX;
}

/** Anstrengung slider: window width in scale points (1–10) */
const SWEAT_WINDOW = 3;
/** Anstrengung slider: step size */
const SWEAT_STEP = 1;
/** Anstrengung slider: max value */
const SWEAT_MAX = 10;

/** Typewriter placeholder phrases for the teacher search input */
const TEACHER_SEARCH_PLACEHOLDERS = [
  "Vorname oder Nachname …",
  "Name Deiner Lieblingslehrerin …",
  "z.B. Eva oder Birgit …",
];

/** Typewriter placeholder phrases for the topic search input */
const TOPIC_SEARCH_PLACEHOLDERS = [
  "Dein Lieblingsthema …",
  "z.B. Rücken, Schultern, Hüfte …",
  "z.B. Entspannung, Balance, Faszien …",
  "Was möchtest du üben?",
];

/** Popup positioning: space between popup edge and where tail can sit */
const POPUP_TAIL_INSET = 14;
/** Popup positioning: gap between button and popup (room for tail) */
const POPUP_GAP = 14;
/** Popup positioning: keep this margin away from viewport edges */
const POPUP_MARGIN = 8;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map level value from DB (1, 2, 2.5, 3) to chip key (1, 2, 3) */
function dbLevelToChip(v: number): number {
  if (v <= 1) return 1;
  if (v <= 2) return 2;
  return 3; // covers 2.5 and 3
}

// ---------------------------------------------------------------------------
// Main Alpine data context
// ---------------------------------------------------------------------------

export function videoFilterBarDataContext() {
  return {
    // ---- Filter state -------------------------------------------------------

    /** Selected teacher UIDs */
    selectedTeachers: [] as number[],
    /** Selected yoga style UIDs */
    selectedStyles: [] as number[],
    /** Selected topic UIDs */
    selectedTopics: [] as number[],
    /** Selected level chips: 1=Anfänger, 2=Mittel, 3=Fortgeschritten */
    selectedLevels: [] as number[],
    /** Selected Praxis (category) UID, 0 = none */
    selectedCategory: 0,

    // Duration window slider (minutes). null = no constraint.
    durMin: null as number | null,
    durMax: null as number | null,
    /** Is the duration slider open? */
    durOpen: false,

    // Anstrengung window slider (1–10 scale). null = no constraint.
    sweatMin: null as number | null,
    sweatMax: null as number | null,
    /** Is the sweat slider open? */
    sweatOpen: false,

    // ---- Dropdown open state ------------------------------------------------
    teacherOpen: false,
    levelOpen: false,
    styleOpen: false,
    topicOpen: false,
    categoryOpen: false,

    // ---- Popup search filters (for long lists) -----------------------------
    teacherFilter: "",
    topicFilter: "",

    // ---- Typewriter placeholders for search inputs -------------------------
    /** Currently visible placeholder text (cycles through suggestions) */
    searchPlaceholder: "",
    /** Internal: pending setTimeout for the typewriter animation */
    _twTimer: null as ReturnType<typeof setTimeout> | null,
    /** Internal: which popup's suggestions are being cycled right now */
    _twActivePopup: "" as "" | "teacher" | "topic",

    /** Whether the 7-filter swiper row is visible (toggled by "Filter verwenden") */
    filtersVisible: false,

    /** Internal: ResizeObserver for the currently open popup (null when closed) */
    _popupResizeObserver: null as ResizeObserver | null,

    /**
     * Internal: cached params from the last _submit(). Used by the
     * MutationObserver to re-patch load-more buttons whenever the legacy
     * ajax.js replaces them (it does that after every load-more click).
     */
    _lastSubmitParams: null as URLSearchParams | null,
    _lastSubmitFetchUrl: "" as string,
    _loadMoreObserverActive: false,

    /**
     * Monotonic ID for each _submit() call. Only the most recent request's
     * response is applied to the DOM — older in-flight responses are dropped
     * via their AbortController. Prevents a race where clicking two filters
     * in quick succession lets the slower earlier request's result overwrite
     * the newer one (→ the newer filter "silently disappeared").
     */
    _submitSeq: 0,
    _submitAbort: null as AbortController | null,

    // ---- Meta ---------------------------------------------------------------
    /** Fetch URL for the filter AJAX action (?type=133) */
    fetchUrl: "",
    /** Searchmode for the hidden form field */
    searchmode: "ALL",
    /** Items per page */
    itemsPerPage: 12,
    /** Total video count from last response */
    totalCount: 0 as number,
    /** Duration maximum from DB (in minutes) */
    durDbMax: 120,
    /** Whether any filter or NL query is active */
    hasActiveFilters: false,

    // ---- Available options (populated from data attributes) -----------------
    availableTeachers: [] as { uid: number; name: string }[],
    availableStyles: [] as { uid: number; title: string }[],
    availableTopics: [] as { uid: number; title: string }[],
    availableCategories: [] as { uid: number; title: string }[],

    // ---- Responsive column count for the grouped Lehrer/Thema lists --------
    // Mirrors the old CSS breakpoint (3 columns ≥768px, 2 below) but is read
    // explicitly in JS now so filteredTeachersGrouped()/filteredTopicsGrouped()
    // can deterministically decide which column gets the extra item when a
    // letter-group has an odd count (see _splitLeftHeavy()). Kept in sync by
    // a resize listener installed in init().
    _wideCols: 3 as number,

    // ---- NL search integration ----------------------------------------------
    /** JSON from nlSearchAction — tells us which filters the NL search found */
    _nlFilters: null as NlFilters | null,
    /** titleHints from the NL search, persisted across chip changes so the
     *  filterAction can rank by intent without re-calling Claude. */
    _nlTitleHints: [] as string[],

    // ---- Slider drag state --------------------------------------------------
    _durDragging: false,
    _durStartX: 0,
    _durStartMin: 0,
    /**
     * True once the user has actually dragged the duration slider during the
     * current popup session. Used to distinguish "popup opened but not touched"
     * (→ auto-initialised durMin/durMax should be discarded on close) from a
     * real selection.
     */
    _durTouched: false,
    _sweatDragging: false,
    _sweatStartX: 0,
    _sweatStartMin: 0,
    /**
     * Same as _durTouched but for the Anstrengung slider.
     */
    _sweatTouched: false,
    /**
     * True for ~200ms after any slider drag ends. Prevents the `click` that
     * fires after `mouseup` from closing the popup when the user released
     * outside the popup bounds.
     */
    _sliderDragJustEnded: false,

    // =========================================================================
    // Lifecycle
    // =========================================================================

    init(this: any) {
      const root = this.$root as HTMLElement;
      this.fetchUrl = root?.dataset?.fetchUrl ?? "";
      this.searchmode = root?.dataset?.searchmode ?? "ALL";
      this.itemsPerPage = Number(root?.dataset?.itemsPerPage ?? 12);
      this.durDbMax = Number(root?.dataset?.durMax ?? 120);
      this.totalCount = Number(root?.dataset?.totalCount ?? 0);

      // Parse available options from data attributes.
      //
      // Defensive de-duplication by `uid`: users have reported seeing the
      // same teacher twice in the Lehrer popup. We could not reproduce this
      // via the backend (DB/SQL/live JSON all came back clean — see
      // docs/dev-handoff/2026-10-02-videosuche-filter-popup-bugs-batch2.md),
      // so this is a safety net rather than a confirmed root-cause fix: if a
      // duplicate uid ever slips into the JSON (stale cache, future data
      // issue, …) it now gets silently collapsed to its first occurrence
      // instead of rendering twice.
      try {
        this.availableTeachers = this._dedupeByUid(
          JSON.parse(root?.dataset?.teachers ?? "[]")
        );
        this.availableStyles = this._dedupeByUid(
          JSON.parse(root?.dataset?.styles ?? "[]")
        );
        this.availableTopics = this._dedupeByUid(
          JSON.parse(root?.dataset?.topics ?? "[]")
        );
        this.availableCategories = this._dedupeByUid(
          JSON.parse(root?.dataset?.categories ?? "[]")
        );
      } catch (e) {
        console.warn("[videoFilterBar] Failed to parse filter options", e);
      }

      // Keep _wideCols in sync with the CSS breakpoint used by
      // .ymh-popup-body--cols (3 columns ≥768px, 2 below — see default.scss).
      const WIDE_COLS_BREAKPOINT = 768;
      const updateWideCols = () => {
        this._wideCols = window.innerWidth >= WIDE_COLS_BREAKPOINT ? 3 : 2;
      };
      updateWideCols();
      window.addEventListener("resize", updateWideCols, { passive: true });

      // ── Pre-selected filters from URL parameters ───────────────────────────
      // When a link on the site carries pre-selected filters (e.g. a teacher
      // page linking to "all videos by Eva"), the listAction sets legacy
      // <input type="hidden" id="init*"> elements. We read them here and apply
      // them to the Alpine state so the filter bar starts with those selections
      // active and fires the first AJAX query automatically.
      this._applyInitFilters();

      // ── Load-More MutationObserver ─────────────────────────────────────────
      // The legacy ajax.js replaces the load-more button node after every
      // click (it takes the new button from the AJAX response). Our patch
      // in _submit() is therefore lost on every subsequent page. This
      // observer re-applies the patch whenever the video list DOM changes.
      this._ensureLoadMoreObserver();

      // ── VIDEOLIST: patch Load-More on init ─────────────────────────────────
      // In VIDEOLIST mode the Load-More button carries data-form="video-searchform",
      // but that legacy form element does not exist in the DOM. Without a patch
      // the button sends no searchmode, so filterAction defaults to ALL and
      // returns every video instead of only the user's favourites.
      // Seed _lastSubmitParams with a minimal VIDEOLIST param set so that
      // _patchNlLoadMoreWithFilters runs on init and on every DOM replacement,
      // ensuring the button always carries searchmode=VIDEOLIST even before the
      // user has interacted with any filter.
      if (this.searchmode === "VIDEOLIST") {
        const baseParams = new URLSearchParams();
        baseParams.set("tx_vayoga_videoall[action]", "filter");
        baseParams.set("tx_vayoga_videoall[controller]", "Video");
        baseParams.set("tx_vayoga_videoall[searchmode]", "VIDEOLIST");
        this._lastSubmitParams = baseParams;
        this._lastSubmitFetchUrl = this.fetchUrl;
        // Patch synchronously so any autoload triggered before the next animation
        // frame (e.g. IntersectionObserver firing immediately) already carries
        // searchmode=VIDEOLIST. The rAF call is a safety net for buttons that
        // are inserted into the DOM after Alpine's init runs.
        this._patchNlLoadMoreWithFilters(baseParams, this.fetchUrl);
        requestAnimationFrame(() => {
          this._patchNlLoadMoreWithFilters(baseParams, this.fetchUrl);
        });
      }

      // ── NL search integration ──────────────────────────────────────────────
      // When the user searches via the NL input, natural-search.ts fires
      // ymh:nl-filters with the parsed filters. We apply them to our own
      // state AND immediately cache the full params for the load-more
      // observer, so page 2+ of an NL search carries every concrete filter
      // (duration, sweat, level, etc.) instead of just "query=..." which
      // would let the server re-interpret the query non-deterministically.
      document.addEventListener("ymh:nl-filters", ((e: CustomEvent) => {
        const detail = e.detail as { nlFiltersJson: string; totalCount: number; query?: string };
        if (!detail?.nlFiltersJson) return;
        this.applyNlFilters(detail.nlFiltersJson, detail.totalCount);
        this._cacheNlParams(detail.nlFiltersJson, detail.query);
      }) as EventListener);

      // Close open popups when clicking/touching outside the filter-bar.
      // We also listen to touchstart so mobile taps are caught.
      const closeOnOutside = (e: Event) => {
        const target = e.target as Node | null;
        if (!target) return;

        // Mobile Lehrer/Thema popup: no "outside" close (see rationale
        // above isMobileWideSearchPopupOpen). The red X / hardware Back
        // remain the only ways to close it there.
        if (isMobileWideSearchPopupOpen()) return;

        // Never close during or right after a slider drag.
        // A drag that ends outside the popup (user drags to edge and releases)
        // fires a `click` event with target=body; without this guard the popup
        // would close immediately on release.
        if (
          (this as any)._durDragging ||
          (this as any)._sweatDragging ||
          (this as any)._sliderDragJustEnded
        ) return;

        // Never close when interacting with a slider (track, handle, fill, labels)
        if ((target as Element).closest?.(
          ".ymh-slider-track, .ymh-slider-handle, .ymh-slider-fill, .ymh-slider-labels, .ymh-slider-steps, .ymh-slider-rail"
        )) return;

        // Check if click is inside the filter-bar or an open popup
        const inRoot = root.contains(target);
        const inPopup = !!document.querySelector(".ymh-popup.is-visible")?.contains(target);
        // Also allow clicks inside the NL-search stack (above the filter-bar)
        const inSearchStack = !!(target as Element).closest?.(".ymh-search-stack");

        if (inPopup) return; // click inside popup → keep open

        if (inRoot || inSearchStack) {
          // Inside the bar: close popups but keep filter-grid open
          // (unless clicking in empty space = not on a button or chip)
          const inBtn = !!(target as Element).closest?.(
            ".ymh-vfilter-btn, .ymh-popup-close, .ymh-chip-remove, .ymh-action-btn, .ymh-clear-all, .ymh-filter-footer"
          );
          if (!inBtn) {
            this._closeAll();
          }
          return;
        }

        // Outside everything → close popups AND fold filter-grid.
        // App mode (<html data-app-mode>): keep the filter-grid open – on a
        // phone every scroll starts with a touch on the list, which would fold
        // the row the user just opened. Popups still close.
        this._closeAll();
        if (!document.documentElement.hasAttribute("data-app-mode")) {
          this.filtersVisible = false;
        }
      };

      document.addEventListener("click", closeOnOutside);
      // touchstart for mobile — skip if touching inside any open popup
      // (sliders, labels, steps, popup body) to prevent premature close.
      document.addEventListener("touchstart", (e: Event) => {
        const target = e.target as Element | null;
        // If touch is inside any visible popup, never close
        const openPopup = document.querySelector(".ymh-popup.is-visible");
        if (openPopup?.contains(target)) return;
        closeOnOutside(e);
      }, { passive: true });

      // ── Popup auto-close behaviour ───────────────────────────────────────
      //
      // The X button (in the popup template) is the only path that reliably
      // resets all reactive state without leaving stale CSS classes on the
      // filter buttons. All other close triggers (scroll, Android Back,
      // iOS edge-swipe) therefore re-use that same path by programmatically
      // clicking the X button of whichever popup is currently visible.

      let popupJustOpened = false;

      const isAnyPopupOpen = (): boolean =>
        (this as any).teacherOpen ||
        (this as any).levelOpen ||
        (this as any).styleOpen ||
        (this as any).topicOpen ||
        (this as any).categoryOpen ||
        (this as any).durOpen ||
        (this as any).sweatOpen;

      // ── Mobile: Lehrer/Thema popup has no meaningful "outside" ──────────
      // On narrow viewports (<640px, matching the .ymh-popup mobile
      // breakpoint in default.scss) the Lehrer/Thema popup is sized to
      // nearly the full screen width/height. Device testing found that the
      // desktop-oriented "scroll or tap outside closes the popup" behaviour
      // there caused real bugs on iOS Safari (erratic closing, occasional
      // broken stacked layout) and made no UX sense to begin with — there
      // is barely any "outside" to tap/scroll into. The smaller popups
      // (Level, Dauer, Yoga-Stil, Anstrengung, Praxis) stay well short of
      // full height even on mobile, so click/scroll-outside-closes remains
      // useful and active for those; this guard is scoped to exactly the
      // two wide/search popups (.ymh-popup--wide in CSS).
      const MOBILE_BREAKPOINT = 640;
      const isMobileWideSearchPopupOpen = (): boolean =>
        window.innerWidth < MOBILE_BREAKPOINT &&
        ((this as any).teacherOpen || (this as any).topicOpen);

      // Click the X of the visible popup. Falls back to _closeAll() if the
      // popup hasn't been positioned yet (.is-visible class set by JS, see
      // _positionPopup).
      const closeViaXButton = () => {
        const closeBtn = document.querySelector<HTMLElement>(
          ".ymh-popup.is-visible .ymh-popup-close"
        );
        if (closeBtn) {
          closeBtn.click();
        } else {
          (this as any)._closeAll();
        }
      };

      // ── Hardware Back / iOS edge-swipe close ────────────────────────────
      // When the user opens a popup we push a "trap" entry into the browser
      // history. Pressing Back (Android hardware button or iOS edge-swipe)
      // then fires popstate here instead of navigating to the previous page.
      // We close the popup using the same code path as the X button.
      //
      // When the popup is closed by other means (X button, click-outside,
      // scroll), we silently consume our trap entry so that the next real
      // Back press navigates the page properly. The _consumingTrap flag
      // prevents popstate from acting on our own history.back() call.
      let _consumingTrap = false;

      const origToggle = (this as any).toggleDropdown.bind(this);
      (this as any).toggleDropdown = (name: string) => {
        const wasAnyOpen = isAnyPopupOpen();
        origToggle(name);
        const isAnyOpenNow = isAnyPopupOpen();

        // Transition "all closed" → "one open": push synchronously so the
        // browser cannot navigate away before our entry is in place.
        if (!wasAnyOpen && isAnyOpenNow && !history.state?.ymhPopup) {
          history.pushState({ ymhPopup: true }, "");
        }

        popupJustOpened = true;
        setTimeout(() => { popupJustOpened = false; }, 150);
      };

      // Wrap _closeAll: consume our trap history entry so the next real Back
      // press navigates the page, not our entry.
      const origCloseAll = (this as any)._closeAll.bind(this);
      (this as any)._closeAll = () => {
        const hadTrap = history.state?.ymhPopup;
        origCloseAll();
        if (hadTrap && !_consumingTrap) {
          _consumingTrap = true;
          history.back();
          // _consumingTrap is reset inside the popstate handler below.
        }
      };

      window.addEventListener("popstate", () => {
        if (_consumingTrap) {
          // Our own history.back() from _closeAll() – ignore.
          _consumingTrap = false;
          return;
        }
        if (isAnyPopupOpen()) {
          closeViaXButton();
        }
        // No popup open → user pressed Back from a clean state, let browser
        // navigate normally.
      });

      // ── Scroll-to-close ─────────────────────────────────────────────────
      // Skip during slider drags (which fire micro-scrolls), during the
      // 150ms grace period right after opening (the tap may register a tiny
      // scroll which would close the popup we just opened), and while the
      // Lehrer/Thema search input is focused.
      //
      // The last guard exists because iOS was observed to close the Thema
      // popup the instant the user tapped its search field. Likely cause:
      // focusing an input triggers browser-native scroll side-effects
      // (scroll-into-view, Safari address-bar collapse) that fire a
      // `scroll` event on `window` with nothing to do with the user
      // intentionally scrolling the page — this generic listener was
      // closing the popup in response. All other close paths (X button,
      // click outside, hardware Back / edge-swipe) stay fully active; only
      // the "any scroll closes" behaviour is suspended for as long as the
      // search field has focus.
      //
      // Not in app mode (<html data-app-mode>): there the popups are bottom
      // sheets with a backdrop (va_yoga_app app.js), closed via X/backdrop/Back;
      // the on-screen keyboard alone triggers window scrolls on Android.
      const appMode = document.documentElement.hasAttribute("data-app-mode");

      const isSearchInputFocused = (): boolean =>
        !!(document.activeElement as HTMLElement | null)?.classList?.contains(
          "ymh-popup-search-input"
        );

      window.addEventListener("scroll", () => {
        if (appMode) return;
        if ((this as any)._durDragging || (this as any)._sweatDragging) return;
        if (popupJustOpened) return;
        if (isSearchInputFocused()) return;
        if (isMobileWideSearchPopupOpen()) return;
        if (isAnyPopupOpen()) {
          closeViaXButton();
        }
      }, { passive: true });

      // ── Reposition open popup when the on-screen keyboard opens/closes ──
      // window.innerHeight does NOT shrink on iOS Safari/Chrome when the
      // keyboard opens (only on Android Chrome), so a popup positioned
      // before the keyboard appeared can end up with its lower part hidden
      // behind it — most noticeable for the Lehrer/Thema search popups,
      // whose search input triggers the keyboard right after opening.
      // The VisualViewport API reports the real visible height on all
      // mobile browsers, so re-running _positionPopup() when it fires
      // keeps the popup (and the search-match row scrolled inside it)
      // within the actually visible area.
      const vv = (window as any).visualViewport;
      if (vv) {
        const repositionOpenPopup = () => {
          const openName = (
            ["teacher", "level", "style", "topic", "category", "dur", "sweat"] as const
          ).find((n) => (this as any)[`${n}Open`]);
          if (openName) {
            (this as any)._positionPopup(openName);
          }
        };
        vv.addEventListener("resize", repositionOpenPopup);
        vv.addEventListener("scroll", repositionOpenPopup);
      }

      // ── Hide the floating Trusted Shops widget on this page ────────────
      // Site-wide via Google Tag Manager (outside our own templates — the
      // TYPO3-rendered badge in Views/Page/Partials/Landing/TrustedShops.html
      // only appears on Landing-layout pages and does NOT render here), the
      // official Trustbadge script injects its own fixed-position elements
      // directly into <body> once Cookiebot grants "preferences" consent.
      // On this page it was reported overlapping the Lehrer/Thema filter
      // popups. We cannot change the GTM trigger from here, so instead we
      // hide it ourselves, scoped to only run where this component mounts
      // (i.e. the video search/listing pages).
      //
      // A plain `el.style.display = "none"` via MutationObserver was tried
      // first but lost a race: the widget's own (React-based) re-renders
      // periodically re-apply ITS OWN inline style, silently undoing our
      // change again afterwards. A real `<style>` rule with `!important`
      // doesn't have this problem — the cascade always prefers an
      // `!important` stylesheet rule over a plain (non-important) inline
      // style, no matter how many times or how late the widget re-renders.
      //
      // Guarded by id so a second init() (e.g. if Alpine ever re-mounts
      // this component on the same page) doesn't keep appending duplicate
      // <style> tags.
      if (!document.getElementById("ymh-trustbadge-hide-style")) {
        const trustbadgeStyle = document.createElement("style");
        trustbadgeStyle.id = "ymh-trustbadge-hide-style";
        trustbadgeStyle.textContent = `
          #MyCustomTrustbadge,
          #lp-trustbadge,
          [id^="trustbadge-container"],
          [id^="minimized-trustbadge-"],
          [id^="trustbadge-minimized-menu-button-"],
          #trustbadge-iframe {
            display: none !important;
          }
        `;
        document.head.appendChild(trustbadgeStyle);
      }

      this._updateHasActiveFilters();
    },

    // =========================================================================
    // NL Search integration
    // =========================================================================

    /**
     * Called by naturalSearchDataContext after a successful NL search.
     * Receives the parsed nlFiltersJson and total count from the AJAX response.
     */
    applyNlFilters(this: any, nlFiltersJson: string, totalCount: number) {
      this.totalCount = totalCount;
      try {
        const nlf: NlFilters = JSON.parse(nlFiltersJson);
        this._nlFilters = nlf;
        this._nlTitleHints = Array.isArray(nlf.titleHints) ? nlf.titleHints : [];

        // Teachers
        this.selectedTeachers = nlf.teachers.map((t) => t.uid);

        // Styles
        this.selectedStyles = nlf.styles.map((s) => s.uid);

        // Topics
        this.selectedTopics = nlf.topics.map((t) => t.uid);

        // Level — Claude may give only levelMin ("Fortgeschritten") or only
        // levelMax ("für Anfänger"). Treat missing bounds as open-ended.
        if (nlf.levelMin !== null || nlf.levelMax !== null) {
          const lo = nlf.levelMin ?? 1;
          const hi = nlf.levelMax ?? 3;
          const chips: number[] = [];
          [1, 2, 3].forEach((c) => {
            const dbMin = c === 3 ? 2.5 : c;
            const dbMax = c;
            if (dbMax >= lo && dbMin <= hi) {
              chips.push(c);
            }
          });
          this.selectedLevels = chips;
        } else {
          this.selectedLevels = [];
        }

        // Duration — jump slider to NL range.
        // The NL result may give a min, a max, or both. We centre the fixed-size
        // DUR_WINDOW around the requested range so the slider reflects what the
        // user asked for (e.g. "so 20 min" → durationMax=25 → window 15-25).
        if (nlf.durationMin !== null || nlf.durationMax !== null) {
          const nlMin = nlf.durationMin ?? 0;
          const nlMax = nlf.durationMax ?? this.durDbMax;
          // Anchor the window at the upper bound when only max was given,
          // at the lower bound when only min was given, else centre it.
          let lo: number;
          if (nlf.durationMin === null && nlf.durationMax !== null) {
            lo = Math.max(0, nlMax - DUR_WINDOW);
          } else if (nlf.durationMin !== null && nlf.durationMax === null) {
            lo = nlMin;
          } else {
            lo = Math.max(0, Math.round((nlMin + nlMax) / 2 - DUR_WINDOW / 2));
          }
          this.durMin = lo;
          this.durMax = Math.min(lo + DUR_WINDOW, this.durDbMax);
          this._snapDurWindow();
        } else {
          this.durMin = null;
          this.durMax = null;
        }

        // Anstrengung
        if (nlf.sweatMin !== null || nlf.sweatMax !== null) {
          this.sweatMin = nlf.sweatMin ?? 1;
          this.sweatMax = nlf.sweatMax ?? SWEAT_MAX;
          this._snapSweatWindow();
        } else {
          this.sweatMin = null;
          this.sweatMax = null;
        }

        // Category
        if (nlf.category) {
          this.selectedCategory = nlf.category;
        } else {
          this.selectedCategory = 0;
        }

        this._updateHasActiveFilters();
      } catch (e) {
        console.warn("[videoFilterBar] Failed to parse nlFilters", e);
      }
    },

    // =========================================================================
    // Popup filtered lists (for search input in long popups)
    // =========================================================================

    /**
     * Removes entries whose `uid` already occurred earlier in the array,
     * keeping the first occurrence. Pure safety net (see init()) — does not
     * fix a known root cause, just prevents a duplicate uid from ever
     * rendering twice in a popup list.
     */
    _dedupeByUid<T extends { uid: number }>(items: T[]): T[] {
      const seen = new Set<number>();
      const result: T[] = [];
      for (const item of items) {
        if (seen.has(item.uid)) continue;
        seen.add(item.uid);
        result.push(item);
      }
      return result;
    },

    /**
     * Splits `items` into `cols` column-arrays, document order preserved,
     * giving the EARLIER columns the larger share when the count doesn't
     * divide evenly (e.g. 3 items / 2 cols → [2, 1], 5 items / 2 cols →
     * [3, 2]). This is the deterministic replacement for relying on the
     * browser's native CSS column-balance algorithm, which optimises for
     * equal column HEIGHT rather than equal item COUNT and can just as
     * easily produce the opposite (right-heavy) split for small/odd groups.
     */
    _splitLeftHeavy<T>(items: T[], cols: number): T[][] {
      const n = items.length;
      const safeCols = Math.max(1, cols);
      const base = Math.floor(n / safeCols);
      const remainder = n % safeCols;
      const result: T[][] = [];
      let idx = 0;
      for (let c = 0; c < safeCols; c++) {
        const count = base + (c < remainder ? 1 : 0);
        result.push(items.slice(idx, idx + count));
        idx += count;
      }
      return result;
    },

    /**
     * Token-based fuzzy match for popup search.
     * Splits both query and haystack on non-letter boundaries (space, dash,
     * slash, punctuation, umlaut handling kept simple) and requires that every
     * query token appears as a PREFIX of some haystack token – or, for tokens
     * shorter than 4 chars, as a substring of the whole haystack.
     *
     * Examples (query → matches):
     *   "morgens"     → "Morgen-Yoga"        ✓ (token "morgens" → stem "morgen" → prefix of "morgen")
     *   "unter"       → "Unterer Rücken"     ✓
     *   "rücken"      → "Rückenyoga"         ✓
     *   "bitt"        → "Bitta Boerger"      ✓
     *   "yoga morgen" → "Morgen-Yoga"        ✓ (both tokens match)
     */
    _fuzzyMatches(haystack: string, query: string): boolean {
      const q = query.trim().toLowerCase();
      if (q === "") return true;
      const hay = haystack.toLowerCase();
      // Cheap fast-path: plain substring match always counts
      if (hay.includes(q)) return true;

      // Split into word tokens (letters only); map umlauts so "rücken" and
      // "ruecken" still cross-match with "Rücken".
      const normalise = (s: string) =>
        s.replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");

      const tokenise = (s: string): string[] =>
        normalise(s.toLowerCase())
          .split(/[^\p{L}0-9]+/u)
          .filter(Boolean);

      const hayTokens = tokenise(hay);
      const queryTokens = tokenise(q);
      if (queryTokens.length === 0) return false;

      // Every query token must match some haystack token (prefix or stem)
      return queryTokens.every((qt) => {
        if (qt.length < 4) {
          // Short token: accept substring anywhere
          return hayTokens.some((ht) => ht.includes(qt));
        }
        // Longer token: accept prefix match, and also a stem match
        // (first len-2 chars) so inflections work ("morgens" → "morg" → "morgen")
        const stem = qt.slice(0, qt.length - 2);
        return hayTokens.some(
          (ht) => ht.startsWith(stem) || ht.startsWith(qt) || ht.includes(qt)
        );
      });
    },

    /**
     * Picks the best-matching item and returns its UID, preferring (in order):
     *   1. exact prefix of whole haystack        ("bitt" beats "birgit" for "Bitta")
     *   2. any word starts with the full query   ("stef" → "Stefanie")
     *   3. stem/fuzzy match via _fuzzyMatches    (last resort)
     * Returns 0 if nothing matches.
     */
    _bestMatchUid(
      items: Array<{ uid: number }>,
      query: string,
      getText: (item: any) => string
    ): number {
      const q = query.trim().toLowerCase();
      if (q === "") return 0;

      const normalise = (s: string) =>
        s.toLowerCase()
          .replace(/ä/g, "ae")
          .replace(/ö/g, "oe")
          .replace(/ü/g, "ue")
          .replace(/ß/g, "ss");
      const qn = normalise(q);

      let exactPrefix: { uid: number } | null = null;
      let wordPrefix: { uid: number } | null = null;
      let fuzzy: { uid: number } | null = null;

      for (const it of items) {
        const text = getText(it);
        const tn = normalise(text);

        if (!exactPrefix && tn.startsWith(qn)) {
          exactPrefix = it;
          break; // best possible hit
        }
        if (!wordPrefix) {
          // Any token in the haystack starts with the full query?
          const words = tn.split(/[^\p{L}0-9]+/u);
          if (words.some((w) => w.startsWith(qn))) {
            wordPrefix = it;
          }
        }
        if (!fuzzy && this._fuzzyMatches(text, query)) {
          fuzzy = it;
        }
      }

      const best = exactPrefix ?? wordPrefix ?? fuzzy;
      return best ? best.uid : 0;
    },

    /** Teachers matching the popup search filter (token-based fuzzy match). */
    filteredTeachers(this: any): Array<{ uid: number; name: string }> {
      const q = String(this.teacherFilter ?? "").trim();
      if (q === "") return this.availableTeachers;
      return this.availableTeachers.filter((t: { name: string }) =>
        this._fuzzyMatches(t.name, q)
      );
    },

    /**
     * UID of the teacher the popup search currently matches best (or 0).
     * Prefers strict prefix matches over stem/fuzzy matches so that
     * "bitt" picks "Bitta" even though "Birgit" would also fuzzy-match.
     */
    teacherSearchMatchUid(this: any): number {
      const q = String(this.teacherFilter ?? "").trim();
      if (q === "") return 0;
      return this._bestMatchUid(this.availableTeachers, q, (t: any) => t.name);
    },

    // =========================================================================
    // Typewriter placeholder for search inputs
    // =========================================================================

    /**
     * Read legacy <input type="hidden" id="init*"> elements that listAction
     * writes when URL parameters carry pre-selected filters (e.g. a teacher
     * page links to /yoga-videos with ?tx_vayoga_videoall[teachers][]=42).
     * Maps the legacy values into the current Alpine filter state and triggers
     * an initial AJAX query so the page loads with those filters active.
     *
     * Supported IDs (same names the old jQuery form.js used):
     *   initTeacher, initYogaStyle, initTopics, initCategory,
     *   initLevelMin + initLevelMax, initSweatFactorMin + initSweatFactorMax,
     *   initDurationMin + initDurationMax
     */
    _applyInitFilters(this: any) {
      const get = (id: string): string | null =>
        (document.getElementById(id) as HTMLInputElement | null)?.value ?? null;

      let hasAny = false;

      // Hash-based filters: #level=3, #category=9, etc.
      // Used by menu links that cannot carry query-params through the proxy.
      const hash = window.location.hash.replace(/^#/, "");
      if (hash) {
        const params = new URLSearchParams(hash);

        const levelFromHash = params.get("level");
        if (levelFromHash) {
          const chip = Number(levelFromHash);
          if (chip >= 1 && chip <= 3) {
            this.selectedLevels = [chip];
            hasAny = true;
          }
        }

        const catFromHash = params.get("category");
        if (catFromHash && Number(catFromHash) > 0) {
          this.selectedCategory = Number(catFromHash);
          hasAny = true;
        }

        // Clean hash from URL without triggering navigation
        if (hasAny) {
          history.replaceState(null, "", window.location.pathname + window.location.search);
        }
      }

      // Teacher: single UID
      const teacher = get("initTeacher");
      if (teacher && Number(teacher) > 0) {
        this.selectedTeachers = [Number(teacher)];
        hasAny = true;
      }

      // Yoga style: single UID
      const style = get("initYogaStyle");
      if (style && Number(style) > 0) {
        this.selectedStyles = [Number(style)];
        hasAny = true;
      }

      // Topic: single UID
      const topic = get("initTopics");
      if (topic && Number(topic) > 0) {
        this.selectedTopics = [Number(topic)];
        hasAny = true;
      }

      // Category (Praxis): numeric ID
      const cat = get("initCategory");
      if (cat && Number(cat) > 0) {
        this.selectedCategory = Number(cat);
        hasAny = true;
      }

      // Level: min+max converted from old continuous range to chip selection.
      // Old system used a continuous 1–3 range (levelMin/Max are already DB
      // values like 1, 2, 3).  We map to chip UIDs (1/2/3) generously:
      //   ≤1 → chip 1 (Anfänger), ~2 → chip 2 (Mittel), ≥2.5 → chip 3 (Fortg.)
      const levelMinRaw = get("initLevelMin");
      const levelMaxRaw = get("initLevelMax");
      if (levelMinRaw !== null && levelMaxRaw !== null) {
        const lMin = Number(levelMinRaw);
        const lMax = Number(levelMaxRaw);
        const chips: number[] = [];
        if (lMin <= 1 && lMax >= 1) chips.push(1);
        if (lMin <= 2 && lMax >= 2) chips.push(2);
        if (lMax >= 2.5 || lMax >= 3)  chips.push(3);
        if (chips.length > 0) {
          this.selectedLevels = chips;
          hasAny = true;
        }
      }

      // Sweat: old scale was 0–100, new UI uses 0–10.
      const sweatMinRaw = get("initSweatFactorMin");
      const sweatMaxRaw = get("initSweatFactorMax");
      if (sweatMinRaw !== null && sweatMaxRaw !== null) {
        const sMin = Math.round(Number(sweatMinRaw) / 10);
        const sMax = Math.round(Number(sweatMaxRaw) / 10);
        this.sweatMin = Math.max(0, Math.min(sMin, 10));
        this.sweatMax = Math.max(0, Math.min(sMax, 10));
        hasAny = true;
      }

      // Duration: old scale was minutes directly, same as new system.
      const durMinRaw = get("initDurationMin");
      const durMaxRaw = get("initDurationMax");
      if (durMinRaw !== null && durMaxRaw !== null) {
        this.durMin = Math.max(0, Number(durMinRaw));
        this.durMax = Number(durMaxRaw);
        hasAny = true;
      }

      if (hasAny) {
        // Show the filter bar so the user can see which filters are active
        this.filtersVisible = true;
        // Run the first query with a small delay so Alpine has hydrated
        setTimeout(() => this._submit(), 100);
      }
    },

    /**
     * Start cycling through placeholder suggestions for the given popup's
     * search input. Runs only while that popup is open.
     */
    _twStart(this: any, popup: "teacher" | "topic") {
      this._twStop();
      this._twActivePopup = popup;
      const phrases =
        popup === "teacher"
          ? TEACHER_SEARCH_PLACEHOLDERS
          : TOPIC_SEARCH_PLACEHOLDERS;
      let idx = 0;

      const runPhrase = (phrase: string) => {
        let pos = 0;
        this.searchPlaceholder = "";
        const typeNext = () => {
          // Stop if popup was closed or user typed into the input
          if (this._twActivePopup !== popup) return;
          const userInput =
            popup === "teacher" ? this.teacherFilter : this.topicFilter;
          if (userInput !== "") return;
          if (pos < phrase.length) {
            this.searchPlaceholder = phrase.slice(0, ++pos);
            this._twTimer = setTimeout(typeNext, 45);
          } else {
            this._twTimer = setTimeout(eraseAll, 1800);
          }
        };
        const eraseAll = () => {
          if (this._twActivePopup !== popup) return;
          const userInput =
            popup === "teacher" ? this.teacherFilter : this.topicFilter;
          if (userInput !== "") return;
          if (this.searchPlaceholder.length > 0) {
            this.searchPlaceholder = this.searchPlaceholder.slice(0, -1);
            this._twTimer = setTimeout(eraseAll, 22);
          } else {
            idx = (idx + 1) % phrases.length;
            this._twTimer = setTimeout(
              () => runPhrase(phrases[idx]),
              400
            );
          }
        };
        typeNext();
      };

      this._twTimer = setTimeout(() => runPhrase(phrases[idx]), 300);
    },

    /** Stop the typewriter and clear the placeholder. */
    _twStop(this: any) {
      if (this._twTimer !== null) {
        clearTimeout(this._twTimer);
        this._twTimer = null;
      }
      this._twActivePopup = "";
      this.searchPlaceholder = "";
    },

    /**
     * Scrolls `target` so it sits ~8px from the top of `body`'s own visible
     * window, preferring a smooth animation.
     *
     * WebKit (iOS Safari + iOS Chrome, which shares Safari's engine) has
     * been observed to silently no-op `Element.scrollTo({behavior:"smooth"})`
     * calls fired from a keystroke handler while the on-screen keyboard is
     * still animating in / the browser's own "scroll focused input into
     * view" behaviour is competing for the same scroll container. Rather
     * than depending on knowing the exact WebKit mechanism, we verify the
     * outcome after the animation should have finished and force an
     * instant (non-animated) correction if the target still isn't in
     * position — this guarantees the match becomes visible on every
     * engine, at the cost of an occasional visible "snap" on the affected
     * browsers instead of a smooth glide.
     *
     * A single 400ms re-check was not enough in practice on iPhone (teacher
     * search reported as "never jumps to the match"): iOS can keep nudging
     * the popup's own position/size for a while after the keyboard opens
     * (e.g. the QuickType suggestion bar changing height as predictions
     * appear/disappear while typing triggers `visualViewport` resize
     * events, which re-run `_positionPopup()` and can shift/reset the
     * popup body's scroll position again, *after* our first correction).
     * We therefore re-verify several times over ~1.1s instead of once —
     * each check is cheap (two getBoundingClientRect() calls) and is a
     * no-op once the position has actually settled.
     *
     * `isStillCurrent` guards against correcting toward a stale target:
     * if the user typed further while a re-check was still pending, a
     * newer call already owns the scroll for the now-current match.
     */
    _scrollMatchIntoView(
      this: any,
      body: HTMLElement,
      target: HTMLElement,
      isStillCurrent: () => boolean
    ) {
      const remainingDelta = () => {
        const bodyRect = body.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        return targetRect.top - bodyRect.top - 8;
      };

      const correctIfNeeded = (behavior: ScrollBehavior): void => {
        const delta = remainingDelta();
        if (Math.abs(delta) < 4) return; // already close enough, nothing to do
        body.scrollTo({ top: body.scrollTop + delta, behavior });
      };

      correctIfNeeded("smooth");

      const RECHECK_DELAYS_MS = [120, 300, 500, 750, 1100];
      for (const delayMs of RECHECK_DELAYS_MS) {
        setTimeout(() => {
          if (!isStillCurrent()) return;
          correctIfNeeded("auto");
        }, delayMs);
      }
    },

    /**
     * Called from the teacher popup's search input on each keystroke.
     * Scrolls the first matching teacher into view inside the popup body
     * (no filtering — the list stays the same so the popup shape doesn't shift).
     */
    onTeacherSearchInput(this: any) {
      const uid = this.teacherSearchMatchUid();
      if (!uid) return;
      requestAnimationFrame(() => {
        const popup = document.querySelector<HTMLElement>(
          '.ymh-popup[data-popup="teacher"]'
        );
        if (!popup) return;
        const body = popup.querySelector<HTMLElement>(".ymh-popup-body");
        const target = popup.querySelector<HTMLElement>(
          `[data-teacher-uid="${uid}"]`
        );
        if (!body || !target) return;
        this._scrollMatchIntoView(
          body,
          target,
          () => this.teacherSearchMatchUid() === uid
        );
      });
    },

    /**
     * Teachers sorted by first name, grouped into per-letter sections. Each
     * section carries its own items already split into `_wideCols`
     * column-arrays (left-heavy: earlier columns get the larger share on an
     * odd count — see _splitLeftHeavy()). The template renders one letter
     * header per section followed by a flex row of these column-arrays,
     * instead of relying on native CSS column-count + column-span, which
     * cannot guarantee which side gets the extra item.
     *
     * Each returned entry: { letter: 'A', id: 'L-A', cols: [[teacher, …], [teacher, …]] }
     *
     * Academic titles like "Dr.", "Prof.", "Prof. Dr." are stripped before
     * sorting and grouping, so "Dr. Wilfried Reuter" is grouped under "W".
     */
    filteredTeachersGrouped(this: any): Array<any> {
      // Strip common academic titles from the start of a name. Repeats so
      // multi-part titles like "Dr. med.", "Prof. Dr. med." are fully removed.
      const stripTitle = (name: string): string => {
        const TITLE_RE = /^(?:Prof\.?|Dr\.?|Dipl\.?|med\.?|mult\.?|hc\.?|h\.c\.?|Mag\.?)\s+/i;
        let n = name.trim();
        // Up to 4 iterations is plenty (Prof. Dr. med. h.c.)
        for (let i = 0; i < 4 && TITLE_RE.test(n); i++) {
          n = n.replace(TITLE_RE, "").trim();
        }
        return n;
      };

      // Always return ALL teachers (search only highlights the first match,
      // doesn't filter the list — keeps popup shape stable)
      const sorted = this.availableTeachers.slice().sort((a: any, b: any) =>
        stripTitle(a.name).localeCompare(stripTitle(b.name), "de")
      );

      // First pass: bucket teachers by their group letter, preserving order.
      const letters: string[] = [];
      const byLetter = new Map<string, any[]>();
      for (const t of sorted) {
        const displayName = stripTitle(t.name);
        const firstChar = (displayName.charAt(0) || "").toUpperCase();
        // Normalise umlauts so Ä/Ö/Ü fall into A/O/U groups (nicer grouping)
        const groupLetter = firstChar
          .replace("Ä", "A")
          .replace("Ö", "O")
          .replace("Ü", "U");
        if (!byLetter.has(groupLetter)) {
          letters.push(groupLetter);
          byLetter.set(groupLetter, []);
        }
        byLetter.get(groupLetter)!.push({
          uid: t.uid,
          name: t.name,
          avatar: t.avatar,
          id: "T-" + t.uid,
        });
      }

      // Second pass: split each letter's teachers into _wideCols columns.
      return letters.map((letter) => ({
        letter,
        id: "L-" + letter,
        cols: this._splitLeftHeavy(byLetter.get(letter)!, this._wideCols),
      }));
    },

    /** Topics matching the popup search filter (token-based fuzzy match). */
    filteredTopics(this: any): Array<{ uid: number; title: string }> {
      const q = String(this.topicFilter ?? "").trim();
      if (q === "") return this.availableTopics;
      return this.availableTopics.filter((t: { title: string }) =>
        this._fuzzyMatches(t.title, q)
      );
    },

    /** UID of the best-matching topic for the popup search, or 0. */
    topicSearchMatchUid(this: any): number {
      const q = String(this.topicFilter ?? "").trim();
      if (q === "") return 0;
      return this._bestMatchUid(this.availableTopics, q, (t: any) => t.title);
    },

    /**
     * Called from the topic popup's search input on each keystroke.
     * Scrolls the first matching topic into view inside the popup body
     * (no filtering — the list stays the same so the popup shape doesn't shift).
     */
    onTopicSearchInput(this: any) {
      const uid = this.topicSearchMatchUid();
      if (!uid) return;
      requestAnimationFrame(() => {
        const popup = document.querySelector<HTMLElement>(
          '.ymh-popup[data-popup="topic"]'
        );
        if (!popup) return;
        const body = popup.querySelector<HTMLElement>(".ymh-popup-body");
        const target = popup.querySelector<HTMLElement>(
          `[data-topic-uid="${uid}"]`
        );
        if (!body || !target) return;
        this._scrollMatchIntoView(
          body,
          target,
          () => this.topicSearchMatchUid() === uid
        );
      });
    },

    /**
     * Topics sorted alphabetically by title, grouped into per-letter sections
     * (German locale, umlauts grouped with their base letter). Same
     * left-heavy column split as filteredTeachersGrouped() — see there for
     * the full rationale.
     *
     * Each returned entry: { letter: 'A', id: 'L-A', cols: [[topic, …], [topic, …]] }
     */
    filteredTopicsGrouped(this: any): Array<any> {
      // Always return ALL topics (search only highlights + scrolls)
      const sorted = this.availableTopics.slice().sort((a: any, b: any) =>
        a.title.localeCompare(b.title, "de")
      );

      const letters: string[] = [];
      const byLetter = new Map<string, any[]>();
      for (const t of sorted) {
        const firstChar = (t.title.charAt(0) || "").toUpperCase();
        const groupLetter = firstChar
          .replace("Ä", "A")
          .replace("Ö", "O")
          .replace("Ü", "U");
        if (!byLetter.has(groupLetter)) {
          letters.push(groupLetter);
          byLetter.set(groupLetter, []);
        }
        byLetter.get(groupLetter)!.push({
          uid: t.uid,
          title: t.title,
          id: "P-" + t.uid,
        });
      }

      return letters.map((letter) => ({
        letter,
        id: "L-" + letter,
        cols: this._splitLeftHeavy(byLetter.get(letter)!, this._wideCols),
      }));
    },

    // =========================================================================
    // Chip display helpers
    // =========================================================================

    /** Returns label strings for currently selected teachers */
    teacherChipLabels(this: any): string[] {
      // When NL filters are active, merge NL labels with any manually added teachers.
      // NL-known teachers use their NL label; additional manual teachers use availableTeachers.
      if (this._nlFilters && this._nlFilters.teachers.length > 0) {
        const nlUids = new Set(this._nlFilters.teachers.map((t: NlFilterChip) => t.uid));
        const nlLabels = this._nlFilters.teachers
          .filter((t: NlFilterChip) => this.selectedTeachers.includes(t.uid))
          .map((t: NlFilterChip) => t.label);
        // Extra teachers added manually (not in NL result)
        const manualLabels = this.availableTeachers
          .filter((t: any) => this.selectedTeachers.includes(t.uid) && !nlUids.has(t.uid))
          .map((t: any) => t.name);
        return [...nlLabels, ...manualLabels];
      }
      return this.availableTeachers
        .filter((t: any) => this.selectedTeachers.includes(t.uid))
        .map((t: any) => t.name);
    },

    styleChipLabels(this: any): string[] {
      if (this._nlFilters && this._nlFilters.styles.length > 0) {
        const nlUids = new Set(this._nlFilters.styles.map((s: NlFilterChip) => s.uid));
        const nlLabels = this._nlFilters.styles
          .filter((s: NlFilterChip) => this.selectedStyles.includes(s.uid))
          .map((s: NlFilterChip) => s.label);
        const manualLabels = this.availableStyles
          .filter((s: any) => this.selectedStyles.includes(s.uid) && !nlUids.has(s.uid))
          .map((s: any) => s.title);
        return [...nlLabels, ...manualLabels];
      }
      return this.availableStyles
        .filter((s: any) => this.selectedStyles.includes(s.uid))
        .map((s: any) => s.title);
    },

    topicChipLabels(this: any): string[] {
      if (this._nlFilters && this._nlFilters.topics.length > 0) {
        const nlUids = new Set(this._nlFilters.topics.map((t: NlFilterChip) => t.uid));
        const nlLabels = this._nlFilters.topics
          .filter((t: NlFilterChip) => this.selectedTopics.includes(t.uid))
          .map((t: NlFilterChip) => t.label);
        const manualLabels = this.availableTopics
          .filter((t: any) => this.selectedTopics.includes(t.uid) && !nlUids.has(t.uid))
          .map((t: any) => t.title);
        return [...nlLabels, ...manualLabels];
      }
      return this.availableTopics
        .filter((t: any) => this.selectedTopics.includes(t.uid))
        .map((t: any) => t.title);
    },

    categoryChipLabel(this: any): string | null {
      if (!this.selectedCategory) return null;
      return this.availableCategories.find((c) => c.uid === this.selectedCategory)?.title ?? null;
    },

    levelLabel(chip: number): string {
      return chip === 1 ? "Anfänger" : chip === 2 ? "Mittel" : "Fortgeschritten";
    },

    /** Human-readable duration range string */
    durLabel(this: any): string {
      if (this.durMin === null && this.durMax === null) return "";
      const lo = this.durMin ?? 0;
      const hi = this.durMax ?? this.durDbMax;
      if (hi >= this.durDbMax) return `${lo}+ Min`;
      return `${lo}–${hi} Min`;
    },

    /** Compact duration label for the active-filter chip: centre ±5 */
    durLabelShort(this: any): string {
      if (this.durMin === null && this.durMax === null) return "";
      const lo = this.durMin ?? 0;
      const hi = this.durMax ?? this.durDbMax;
      if (hi >= this.durDbMax) return `${lo}+ Min`;
      const mid = Math.round((lo + hi) / 2);
      return `${mid} Min (±5)`;
    },

    /** Human-readable Anstrengung range string */
    sweatLabel(this: any): string {
      if (this.sweatMin === null && this.sweatMax === null) return "";
      return `${this.sweatMin ?? 1}–${this.sweatMax ?? SWEAT_MAX}`;
    },

    /** Compact sweat label for the active-filter chip: centre ±1 */
    sweatLabelShort(this: any): string {
      if (this.sweatMin === null && this.sweatMax === null) return "";
      const lo = this.sweatMin ?? 0;
      const hi = this.sweatMax ?? SWEAT_MAX;
      const mid = Math.round((lo + hi) / 2);
      return `${mid} (±1)`;
    },

    // =========================================================================
    // Filter interaction
    // =========================================================================

    toggleTeacher(this: any, uid: number) {
      const idx = this.selectedTeachers.indexOf(uid);
      if (idx === -1) this.selectedTeachers.push(uid);
      else this.selectedTeachers.splice(idx, 1);
      this._submit();
    },

    toggleStyle(this: any, uid: number) {
      const idx = this.selectedStyles.indexOf(uid);
      if (idx === -1) this.selectedStyles.push(uid);
      else this.selectedStyles.splice(idx, 1);
      this._submit();
    },

    toggleTopic(this: any, uid: number) {
      const idx = this.selectedTopics.indexOf(uid);
      if (idx === -1) this.selectedTopics.push(uid);
      else this.selectedTopics.splice(idx, 1);
      this._submit();
    },

    toggleLevel(this: any, chip: number) {
      const idx = this.selectedLevels.indexOf(chip);
      if (idx === -1) this.selectedLevels.push(chip);
      else this.selectedLevels.splice(idx, 1);
      this._submit();
    },

    selectCategory(this: any, uid: number) {
      this.selectedCategory = this.selectedCategory === uid ? 0 : uid;
      // No longer auto-closes the popup (used to set categoryOpen = false
      // here) — made consistent with Yoga-Stil: the popup now stays open
      // after a selection, with the "Ergebnisse anzeigen" footer button
      // (see Search.html) as the explicit way to close it.
      this._submit();
    },

    removeTeacherChip(this: any, uid: number) {
      this.selectedTeachers = this.selectedTeachers.filter((t: number) => t !== uid);
      this._submit();
    },

    removeStyleChip(this: any, uid: number) {
      this.selectedStyles = this.selectedStyles.filter((s: number) => s !== uid);
      this._submit();
    },

    removeTopicChip(this: any, uid: number) {
      this.selectedTopics = this.selectedTopics.filter((t: number) => t !== uid);
      this._submit();
    },

    clearDuration(this: any) {
      this.durMin = null;
      this.durMax = null;
      this._submit();
    },

    clearSweat(this: any) {
      this.sweatMin = null;
      this.sweatMax = null;
      this._submit();
    },

    /**
     * "Alles löschen" (red button): clears every manual filter chip AND
     * the free-text word search above it.
     *
     * Deliberately asymmetric with the grey X in the word search (see
     * natural-search.ts's reset()), which only clears the search text and
     * leaves these filters untouched — user feedback (2026-10-05) was that
     * clearing the text alone had been unexpectedly wiping the filters too,
     * which this button is now the single place that still does on purpose.
     *
     * Reuses the existing `ymh:nl-reset` event to tell the word-search
     * component to clear itself too (see its `@ymh:nl-reset.document`
     * listener in Search.html) rather than reaching into its state
     * directly from here.
     */
    clearAll(this: any) {
      this.selectedTeachers = [];
      this.selectedStyles = [];
      this.selectedTopics = [];
      this.selectedLevels = [];
      this.selectedCategory = 0;
      this.durMin = null;
      this.durMax = null;
      this.sweatMin = null;
      this.sweatMax = null;
      this._nlFilters = null;
      this._nlTitleHints = [];
      this._closeAll();
      this._updateHasActiveFilters();
      // Also reset NL search state
      window.__ymhNlSearch = undefined;
      document.querySelector(".nl-search-hidden-form")?.remove();
      // Tells the word-search component (natural-search.ts) to also clear
      // its own text/UI state — see @ymh:nl-reset.document in Search.html.
      document.dispatchEvent(new CustomEvent("ymh:nl-reset"));
      this._submit();
    },

    // =========================================================================
    // Duration window slider
    // =========================================================================

    /**
     * Map a minute value to its axis position (0..1) using the non-linear
     * mapping. Exposed to Fluid templates so axis labels align with the fill.
     */
    durAxisPct(this: any, minutes: number): number {
      return durMinToPct(minutes);
    },

    /**
     * Returns slider position 0–1 for the left edge of the window using the
     * non-linear axis mapping (0–60 min gets more horizontal space).
     */
    durWindowPos(this: any): number {
      const lo = this.durMin ?? 0;
      if (lo >= DUR_UI_MAX) {
        // "120+" bucket: render as a narrow window in the last 5% segment
        return DUR_MAX_PCT;
      }
      return durMinToPct(lo);
    },

    /**
     * Returns slider window width as fraction of the axis using the non-linear
     * mapping. The window width is NOT constant in screen units because a
     * 10-min window under 60 min is wider than above 60.
     */
    durWindowWidth(this: any): number {
      const lo = this.durMin ?? 0;
      if (lo >= DUR_UI_MAX) return 1 - DUR_MAX_PCT; // "120+" = last segment
      const hi = this.durMax ?? this.durDbMax;
      const leftPct = durMinToPct(lo);
      const rightPct = hi >= DUR_UI_MAX ? 1 : durMinToPct(hi);
      return Math.max(0, rightPct - leftPct);
    },

    durDragStart(this: any, e: MouseEvent | TouchEvent) {
      this._durDragging = true;
      this._durTouched = true;
      const clientX = getClientX(e);
      this._durStartX = clientX;

      // Tap-to-jump: previously, pressing down anywhere on the track just
      // grabbed the EXISTING window (or initialised a default 0-10 one,
      // see the template) wherever it already was — the user had to drag
      // it across from there. Users expected tapping directly on a spot
      // (e.g. "30") to jump the selection there immediately, same as the
      // Anstrengung slider should also do. So: compute the window centred
      // under the initial touch point right here, before any drag move
      // happens — this makes a plain tap-and-release already jump, and
      // seeds _durStartMin so a subsequent drag continues smoothly from
      // this new position (grabOffsetPct in durDragMove ends up ~0).
      const track = this.$refs.durTrack as HTMLElement;
      if (track) {
        const trackRect = track.getBoundingClientRect();
        if (trackRect.width > 0) {
          const cursorPct = (clientX - trackRect.left) / trackRect.width;
          let newMin = durPctToMin(cursorPct) - DUR_WINDOW / 2;
          newMin = Math.round(newMin / DUR_STEP) * DUR_STEP;
          newMin = Math.max(0, Math.min(newMin, DUR_UI_MAX - DUR_WINDOW));
          if (newMin + DUR_WINDOW >= DUR_UI_MAX) {
            this.durMin = DUR_UI_MAX;
            this.durMax = this.durDbMax;
          } else {
            this.durMin = newMin;
            this.durMax = newMin + DUR_WINDOW;
          }
        }
      }
      this._durStartMin = this.durMin ?? 0;
      e.preventDefault();
    },

    durDragMove(this: any, e: MouseEvent | TouchEvent) {
      if (!this._durDragging) return;
      // Guard: abort drag if the dur popup is no longer open.
      // We check this.durOpen (Alpine state) instead of trackWidth because
      // Alpine re-renders asynchronously — the DOM may still show the popup
      // as visible for one frame after _closeAll() is called.
      if (!this.durOpen) {
        this._durDragging = false;
        return;
      }
      const clientX = getClientX(e);
      const track = (this.$refs.durTrack as HTMLElement);
      if (!track) return;
      const trackRect = track.getBoundingClientRect();
      if (trackRect.width <= 0) {
        this._durDragging = false;
        return;
      }
      // Convert cursor X directly to an axis percentage, then map through the
      // non-linear function to minutes. This is more intuitive than adding
      // pixel deltas because the axis is not linear – a 10-min window changes
      // width as the user drags across the breakpoint at 60 min.
      const cursorPct = (clientX - trackRect.left) / trackRect.width;
      // The user grabs the centre of the window, so the left edge is offset
      // by half the window's screen-width (computed from start values).
      const startLeftPct = durMinToPct(this._durStartMin);
      const startRightPct = durMinToPct(this._durStartMin + DUR_WINDOW);
      const startCenterPct = (startLeftPct + startRightPct) / 2;
      const startCursorPct = (this._durStartX - trackRect.left) / trackRect.width;
      const grabOffsetPct = startCursorPct - startCenterPct;
      // New centre in axis space, then convert to new left edge in minutes.
      const newCenterPct = cursorPct - grabOffsetPct;
      // Round-trip through pctToMin to get a minute value, snap to DUR_STEP.
      let newMin = durPctToMin(newCenterPct) - DUR_WINDOW / 2;
      newMin = Math.round(newMin / DUR_STEP) * DUR_STEP;
      newMin = Math.max(0, Math.min(newMin, DUR_UI_MAX - DUR_WINDOW));
      // Right-edge snap: if the fill reaches DUR_UI_MAX, switch to "120+" bucket.
      if (newMin + DUR_WINDOW >= DUR_UI_MAX) {
        this.durMin = DUR_UI_MAX;
        this.durMax = this.durDbMax;
      } else {
        this.durMin = newMin;
        this.durMax = newMin + DUR_WINDOW;
      }
    },

    durDragEnd(this: any) {
      if (!this._durDragging) return;
      this._durDragging = false;
      // Block the click event that fires right after mouseup from triggering
      // closeOnOutside when the drag ended outside the popup bounds.
      this._sliderDragJustEnded = true;
      setTimeout(() => { this._sliderDragJustEnded = false; }, 200);
      this._submit();
    },

    _snapDurWindow(this: any) {
      if (this.durMin === null) return;
      let lo = Math.round(this.durMin / DUR_STEP) * DUR_STEP;
      lo = Math.max(0, Math.min(lo, this.durDbMax - DUR_WINDOW));
      this.durMin = lo;
      this.durMax = lo + DUR_WINDOW;
    },

    // =========================================================================
    // Anstrengung window slider
    // =========================================================================

    /**
     * Fill-Position auf der visuellen Achse (0..SWEAT_MAX). UI-Skala beginnt
     * bei 0 damit auch die niedrigste Anstrengungsstufe (viele Meditationen,
     * sweat=0 in DB) per Range-Daumen erreichbar ist. Die Achse zeigt Ticks
     * bei 0,2,4,6,8,10.
     */
    sweatWindowPos(this: any): number {
      const lo = this.sweatMin ?? 0;
      return lo / SWEAT_MAX;
    },

    sweatWindowWidth(this: any): number {
      const lo = this.sweatMin ?? 0;
      const hi = this.sweatMax ?? SWEAT_MAX;
      return (hi - lo) / SWEAT_MAX;
    },

    sweatDragStart(this: any, e: MouseEvent | TouchEvent) {
      this._sweatDragging = true;
      this._sweatTouched = true;
      const clientX = getClientX(e);
      this._sweatStartX = clientX;

      // Tap-to-jump — same reasoning as durDragStart(): jump the window to
      // sit centred under the initial touch point immediately, instead of
      // requiring the user to drag it over from wherever it was.
      const track = this.$refs.sweatTrack as HTMLElement;
      if (track) {
        const trackRect = track.getBoundingClientRect();
        if (trackRect.width > 0) {
          const cursorPct = (clientX - trackRect.left) / trackRect.width;
          let newMin = cursorPct * SWEAT_MAX - (SWEAT_WINDOW - 1) / 2;
          newMin = Math.round(newMin / SWEAT_STEP) * SWEAT_STEP;
          newMin = Math.max(0, Math.min(newMin, SWEAT_MAX - (SWEAT_WINDOW - 1)));
          this.sweatMin = newMin;
          this.sweatMax = newMin + SWEAT_WINDOW - 1;
        }
      }
      this._sweatStartMin = this.sweatMin ?? 1;
      e.preventDefault();
    },

    sweatDragMove(this: any, e: MouseEvent | TouchEvent) {
      if (!this._sweatDragging) return;
      // Guard: abort drag if the sweat popup is no longer open (same race-condition
      // fix as durDragMove — Alpine re-renders asynchronously).
      if (!this.sweatOpen) {
        this._sweatDragging = false;
        return;
      }
      const clientX = getClientX(e);
      const track = (this.$refs.sweatTrack as HTMLElement);
      if (!track) return;
      const trackWidth = track.getBoundingClientRect().width;
      if (trackWidth <= 0) {
        this._sweatDragging = false;
        return;
      }
      const deltaPx = clientX - this._sweatStartX;
      // Track maps to UI scale 0..SWEAT_MAX (10 units of travel).
      const deltaSteps = (deltaPx / trackWidth) * SWEAT_MAX;
      let newMin = this._sweatStartMin + deltaSteps;
      newMin = Math.round(newMin / SWEAT_STEP) * SWEAT_STEP;
      // lo can be 0..(SWEAT_MAX - SWEAT_WINDOW + 1) = 0..8 (window of 3 values: lo, lo+1, lo+2)
      newMin = Math.max(0, Math.min(newMin, SWEAT_MAX - (SWEAT_WINDOW - 1)));
      this.sweatMin = newMin;
      this.sweatMax = newMin + SWEAT_WINDOW - 1;
    },

    sweatDragEnd(this: any) {
      if (!this._sweatDragging) return;
      this._sweatDragging = false;
      // Block the click event that fires right after mouseup from triggering
      // closeOnOutside when the drag ended outside the popup bounds.
      this._sliderDragJustEnded = true;
      setTimeout(() => { this._sliderDragJustEnded = false; }, 200);
      this._submit();
    },

    _snapSweatWindow(this: any) {
      if (this.sweatMin === null) return;
      let lo = Math.round(this.sweatMin / SWEAT_STEP) * SWEAT_STEP;
      lo = Math.max(1, Math.min(lo, SWEAT_MAX - SWEAT_WINDOW + 1));
      this.sweatMin = lo;
      this.sweatMax = lo + SWEAT_WINDOW - 1;
    },

    // =========================================================================
    // Submission
    // =========================================================================

    async _submit(this: any) {
      this._updateHasActiveFilters();

      // Race-condition guard: every submit gets a monotonic id. If a newer
      // submit starts before the current one finishes, we abort the in-flight
      // fetch and drop its response. Prevents "filter silently disappears"
      // when users click two filters quickly.
      const seq = ++this._submitSeq;
      if (this._submitAbort) {
        this._submitAbort.abort();
      }
      const abort = new AbortController();
      this._submitAbort = abort;

      const params = new URLSearchParams();

      // Once the NL search has been interpreted and the filter chips are
      // set, subsequent _submit() calls (chip add/remove) route through
      // the regular filterAction. This avoids re-calling Claude on every
      // chip change (non-deterministic results + hard-cut inconsistencies).
      // The NL query text stays in the search bar for the user to see, but
      // the server only applies the concrete filter chips, not the free text.
      params.set("tx_vayoga_videoall[action]", "filter");

      params.set("tx_vayoga_videoall[controller]", "Video");
      params.set("tx_vayoga_videoall[searchmode]", this.searchmode);
      params.set("tx_vayoga_videoall[itemsPerPage]", String(this.itemsPerPage));
      params.set("tx_vayoga_videoall[page]", "0");

      this.selectedTeachers.forEach((uid: number) =>
        params.append("tx_vayoga_videoall[teachers][]", String(uid))
      );
      this.selectedStyles.forEach((uid: number) =>
        params.append("tx_vayoga_videoall[styles][]", String(uid))
      );
      this.selectedTopics.forEach((uid: number) =>
        params.append("tx_vayoga_videoall[topics][]", String(uid))
      );
      this.selectedLevels.forEach((chip: number) =>
        params.append("tx_vayoga_videoall[levels][]", String(chip))
      );

      if (this.selectedCategory) {
        params.set("tx_vayoga_videoall[category]", String(this.selectedCategory));
      }

      if (this.durMin !== null) {
        params.set("tx_vayoga_videoall[duration_min]", String(this.durMin));
        params.set("tx_vayoga_videoall[duration_max]", String(this.durMax ?? this.durDbMax));
      }

      if (this.sweatMin !== null) {
        params.set("tx_vayoga_videoall[sweat_min]", String(this.sweatMin));
        params.set("tx_vayoga_videoall[sweat_max]", String(this.sweatMax ?? SWEAT_MAX));
      }

      // Pass through NL-derived titleHints so filterAction can rank by
      // intent without re-calling Claude. Cleared on reset/clearAll.
      if (this._nlTitleHints && this._nlTitleHints.length > 0) {
        this._nlTitleHints.forEach((hint: string) =>
          params.append("tx_vayoga_videoall[title_hints][]", hint)
        );
      }

      try {
        const response = await fetch(this.fetchUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
          signal: abort.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // Discard if a newer submit has started in the meantime.
        if (seq !== this._submitSeq) return;

        const html = await response.text();

        // Check once more after the (async) body read.
        if (seq !== this._submitSeq) return;

        const doc = new DOMParser().parseFromString(html, "text/html");
        const moreItemsEl = doc.querySelector(".more-items");
        const currentItems = document.querySelector(".tx-va-yoga-video-list-items .items");

        if (moreItemsEl && currentItems) {
          currentItems.innerHTML = moreItemsEl.innerHTML;

          // Extract totalCount from response if present
          const countEl = doc.querySelector("[data-total-count]");
          if (countEl) {
            this.totalCount = Number(countEl.getAttribute("data-total-count") ?? 0);
          }

          // Cache this submit's params for the MutationObserver, so every
          // time the legacy ajax.js replaces the load-more button after a
          // click, we can re-apply the full-filter patch automatically.
          const nlState = (window as any).__ymhNlSearch as { fetchUrl?: string; query?: string } | undefined;
          const patchFetchUrl = nlState?.fetchUrl ?? this.fetchUrl;
          this._lastSubmitParams = params;
          this._lastSubmitFetchUrl = patchFetchUrl;

          // Build the full-filter hidden form BEFORE calling
          // initLoadMoreItemsButtons so the Load-More button carries ALL
          // current filter params (duration, level, sweat, teacher, etc.)
          // for subsequent pages.
          this._patchNlLoadMoreWithFilters(params, patchFetchUrl);

          // Re-init legacy load-more buttons + images (after form is patched)
          (window as any).varioous?.ajax_cl?.initLoadMoreItemsButtons?.();
          (window as any).varioous?.image_cl?.preloadImages?.();

          // Re-apply patch after initLoadMoreItemsButtons in case it reset the buttons
          requestAnimationFrame(() => {
            this._patchNlLoadMoreWithFilters(params, patchFetchUrl);
          });
        }
      } catch (e: any) {
        // Silently swallow aborts from the race-condition guard; they are
        // expected when the user clicks a new filter before the previous
        // request resolved.
        if (e?.name === "AbortError") return;
        console.error("[videoFilterBar] submit failed:", e);
      }
    },

    /**
     * Build a full param set for the load-more button after an NL search.
     * Includes the NL query AND all concrete filters the server derived
     * from it (duration, sweat, level …), so page 2+ gets the exact same
     * filter constraints the user sees on page 1 — no re-interpretation
     * by the Claude API on every page.
     */
    _cacheNlParams(this: any, nlFiltersJson: string, query: string | undefined) {
      let nlf: NlFilters;
      try {
        nlf = JSON.parse(nlFiltersJson);
      } catch (e) {
        return;
      }
      const q = query ?? (window as any).__ymhNlSearch?.query ?? "";
      const fetchUrl = (window as any).__ymhNlSearch?.fetchUrl ?? this.fetchUrl;

      const params = new URLSearchParams();
      params.set("tx_vayoga_videoall[action]", "nlSearch");
      params.set("tx_vayoga_videoall[controller]", "Video");
      params.set("tx_vayoga_videoall[query]", q);
      params.set("tx_vayoga_videoall[searchmode]", this.searchmode);

      // Concrete filter values from the NL result. Names must match the
      // manual-filter arguments in nlSearchAction so the server recognises
      // them and skips the Claude re-interpretation for these dimensions.
      (nlf.teachers || []).forEach((t: { uid: number }) =>
        params.append("tx_vayoga_videoall[teachers][]", String(t.uid))
      );
      (nlf.styles || []).forEach((s: { uid: number }) =>
        params.append("tx_vayoga_videoall[styles][]", String(s.uid))
      );
      (nlf.topics || []).forEach((t: { uid: number }) =>
        params.append("tx_vayoga_videoall[topics][]", String(t.uid))
      );
      if (nlf.durationMin !== null && nlf.durationMin !== undefined) {
        params.set("tx_vayoga_videoall[duration_min]", String(nlf.durationMin));
      }
      if (nlf.durationMax !== null && nlf.durationMax !== undefined) {
        params.set("tx_vayoga_videoall[duration_max]", String(nlf.durationMax));
      }
      if (nlf.sweatMin !== null && nlf.sweatMin !== undefined) {
        params.set("tx_vayoga_videoall[sweat_min]", String(nlf.sweatMin));
      }
      if (nlf.sweatMax !== null && nlf.sweatMax !== undefined) {
        params.set("tx_vayoga_videoall[sweat_max]", String(nlf.sweatMax));
      }
      if (this.selectedLevels && this.selectedLevels.length > 0) {
        this.selectedLevels.forEach((chip: number) =>
          params.append("tx_vayoga_videoall[levels][]", String(chip))
        );
      }

      this._lastSubmitParams = params;
      this._lastSubmitFetchUrl = fetchUrl;

      // Immediately patch the button + hidden form so the very next
      // load-more click uses these params (observer handles later replacements).
      this._patchNlLoadMoreWithFilters(params, fetchUrl);
      requestAnimationFrame(() =>
        this._patchNlLoadMoreWithFilters(params, fetchUrl)
      );
    },

    /**
     * Observes the video list for DOM changes (append from load-more). When
     * the legacy ajax.js replaces the load-more button, we re-apply the
     * filter-aware patch so page 2+ still carries every active filter.
     */
    _ensureLoadMoreObserver(this: any) {
      if (this._loadMoreObserverActive) return;
      const container = document.querySelector(".tx-va-yoga-video-list-items");
      if (!container) return;
      new MutationObserver(() => {
        if (!this._lastSubmitParams) return;
        // Defer slightly so ajax.js finishes its own DOM manipulation first
        setTimeout(() => {
          if (this._lastSubmitParams) {
            this._patchNlLoadMoreWithFilters(
              this._lastSubmitParams,
              this._lastSubmitFetchUrl || this.fetchUrl,
            );
          }
        }, 20);
      }).observe(container, { childList: true, subtree: true });
      this._loadMoreObserverActive = true;
    },

    /**
     * After a filter-bar _submit() that routes through nlSearchAction,
     * update the hidden form used by the load-more button so that pagination
     * includes ALL currently active filters (NL query + manual additions).
     */
    _patchNlLoadMoreWithFilters(this: any, params: URLSearchParams, fetchUrl: string) {
      let hiddenForm = document.querySelector(".nl-search-hidden-form") as HTMLFormElement | null;
      if (!hiddenForm) {
        hiddenForm = document.createElement("form");
        hiddenForm.className = "nl-search-hidden-form";
        hiddenForm.style.display = "none";
        document.body.appendChild(hiddenForm);
      }

      // Rebuild hidden form inputs from the params used for this request
      // (these already contain the NL query + all manual filters)
      hiddenForm.innerHTML = "";
      params.forEach((value, key) => {
        // Skip page (load-more sets its own page) and itemsPerPage
        if (key === "tx_vayoga_videoall[page]" || key === "tx_vayoga_videoall[itemsPerPage]") return;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        hiddenForm!.appendChild(input);
      });

      // Point every load-more button at the correct endpoint + form
      document.querySelectorAll<HTMLElement>(".tx-va-yoga-video-list-items .load-more-items").forEach((el) => {
        el.dataset.url = fetchUrl;
        el.dataset.form = "nl-search-hidden-form";
      });
    },

    // =========================================================================
    // Internal helpers
    // =========================================================================

    _closeAll(this: any) {
      this.teacherOpen = false;
      this.levelOpen = false;
      this.styleOpen = false;
      this.topicOpen = false;
      this.categoryOpen = false;
      this.durOpen = false;
      this.sweatOpen = false;

      // Bug fix (found during device-test follow-up, 2026-10-05): the
      // "is-visible" class is added by _positionPopup() when a popup opens,
      // but was never removed again on close — it only gets removed from a
      // given popup the next time THAT SAME popup is re-positioned. If a
      // different popup is opened next, document.querySelector(".ymh-popup
      // .is-visible") (used by closeViaXButton(), the outside-click guard,
      // etc. to find "the currently open popup") could match the WRONG,
      // already-closed popup if it happens to sit earlier in DOM order than
      // the real one — causing inconsistent/wrong-popup closing behaviour.
      // Removing the class from every popup here guarantees at most one
      // popup ever carries it at a time.
      document.querySelectorAll<HTMLElement>(".ymh-popup.is-visible").forEach(
        (el) => el.classList.remove("is-visible")
      );

      // Reset slider drag state so a pending drag doesn't corrupt values
      // when the popup closes mid-drag (e.g. user scrolls while dragging).
      this._durDragging = false;
      this._sweatDragging = false;

      // If the user opened a slider popup but never dragged the handle,
      // discard the auto-initialised values so the filter does not appear
      // as "selected" when nothing was actually chosen.
      // Note: _durTouched / _sweatTouched are reset to false when the *next*
      // popup opens (in toggleDropdown), not here, so that this check sees
      // the correct value even when _closeAll is called by toggleDropdown
      // before opening a new popup.
      if (!this._durTouched) {
        this.durMin = null;
        this.durMax = null;
      }
      if (!this._sweatTouched) {
        this.sweatMin = null;
        this.sweatMax = null;
      }

      // Stop the typewriter placeholder cycle
      this._twStop();

      // Reset the Lehrer/Thema search fields and scroll position so the
      // popup always starts fresh (empty field, scrolled to the top/"A")
      // the next time it's opened. Device testing found both left over
      // from the previous session: a stale search term and the scroll
      // position from wherever the user last scrolled to — neither is
      // expected when re-opening a popup from scratch. The DOM node isn't
      // destroyed when x-show hides it, so without this the scrollTop of
      // .ymh-popup-body simply persists across opens.
      this.teacherFilter = "";
      this.topicFilter = "";
      (["teacher", "topic"] as const).forEach((name) => {
        const body = document.querySelector<HTMLElement>(
          `.ymh-popup[data-popup="${name}"] .ymh-popup-body`
        );
        if (body) body.scrollTop = 0;
      });

      // Disconnect ResizeObserver when all popups are closed
      if (this._popupResizeObserver) {
        this._popupResizeObserver.disconnect();
        this._popupResizeObserver = null;
      }
    },

    _updateHasActiveFilters(this: any) {
      this.hasActiveFilters =
        this.selectedTeachers.length > 0 ||
        this.selectedStyles.length > 0 ||
        this.selectedTopics.length > 0 ||
        this.selectedLevels.length > 0 ||
        this.selectedCategory > 0 ||
        this.durMin !== null ||
        this.sweatMin !== null;
    },

    /**
     * Human-readable summary of currently active filters.
     * Uses the same *ChipLabels() helpers as the filter buttons so that
     * NL-search-only teachers (stored in _nlFilters) are still resolved.
     */
    activeFilterSummary(this: any): string {
      const parts: string[] = [];

      if (this.selectedTeachers.length) {
        parts.push(...this.teacherChipLabels());
      }
      if (this.selectedStyles.length) {
        parts.push(...this.styleChipLabels());
      }
      if (this.selectedTopics.length) {
        parts.push(...this.topicChipLabels());
      }
      if (this.selectedLevels.length) {
        const lvlNames = ["", "Anfänger", "Mittel", "Fortgeschritten"];
        parts.push(
          ...this.selectedLevels
            .slice()
            .sort()
            .map((l: number) => lvlNames[l] ?? `Level ${l}`)
        );
      }
      if (this.selectedCategory > 0) {
        const cat = this.availableCategories.find((c: any) => c.uid === this.selectedCategory);
        if (cat) parts.push(cat.title);
      }
      if (this.durMin !== null && typeof this.durLabel === "function") {
        parts.push(this.durLabel());
      } else if (this.durMin !== null) {
        parts.push(`${this.durMin}–${this.durMax} Min`);
      }
      if (this.sweatMin !== null && typeof this.sweatLabel === "function") {
        parts.push(this.sweatLabel());
      } else if (this.sweatMin !== null) {
        parts.push(`Anstrengung ${this.sweatMin}–${this.sweatMax}`);
      }
      return parts.join(", ");
    },

    toggleDropdown(this: any, name: "teacher" | "level" | "style" | "topic" | "category" | "dur" | "sweat") {
      const map: Record<string, string> = {
        teacher: "teacherOpen",
        level: "levelOpen",
        style: "styleOpen",
        topic: "topicOpen",
        category: "categoryOpen",
        dur: "durOpen",
        sweat: "sweatOpen",
      };
      const key = map[name];
      const wasOpen = (this as any)[key];
      this._closeAll();
      (this as any)[key] = !wasOpen;

      // When opening a slider popup, initialise the range to "at the left edge"
      // so the user sees a draggable handle immediately (no blank rail).
      if (!wasOpen) {
        if (name === "dur") {
          // Reset the "touched" flag for this new popup session.
          // If a real selection is already active (durMin !== null after
          // _closeAll didn't clear it), treat it as pre-touched so _closeAll
          // won't discard it when the user closes without dragging again.
          this._durTouched = this.durMin !== null;
          if (this.durMin === null) {
            this.durMin = 0;
            this.durMax = DUR_WINDOW;
          }
        } else if (name === "sweat") {
          // Same logic as above for the Anstrengung slider.
          this._sweatTouched = this.sweatMin !== null;
          if (this.sweatMin === null) {
            this.sweatMin = 0;
            this.sweatMax = SWEAT_WINDOW - 1;
          }
        } else if (name === "teacher" || name === "topic") {
          // Start cycling search placeholder suggestions.
          //
          // NOTE: we deliberately do NOT auto-focus the search input here
          // (an earlier version did, to let the user start typing without
          // an extra tap). Device testing found that this opened the
          // on-screen keyboard immediately on popup open, covering half the
          // list before the user had even decided to search — the keyboard
          // should only appear once the user actually taps into the field
          // themselves. See the input's `@focus` handler in Search.html for
          // the adaptive repositioning that now runs from there instead.
          this._twStart(name);
        }
      }

      // Position the popup as a speech bubble next to its button.
      // Wait two animation frames so Alpine has rendered all templates,
      // then attach a ResizeObserver in case the content grows further
      // (e.g. when a search filter reveals more entries).
      if (!wasOpen) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._positionPopup(name);
            this._watchPopupResize(name);
          });
        });
      }
    },

    /**
     * Installs a one-shot ResizeObserver that repositions the popup whenever
     * its size changes (e.g. late-rendered template items, content filter).
     * The observer is disconnected automatically when the popup closes.
     */
    _watchPopupResize(this: any, name: string) {
      const popup = document.querySelector<HTMLElement>(
        `.ymh-popup[data-popup="${name}"]`
      );
      if (!popup || typeof ResizeObserver === "undefined") return;

      // Disconnect any previous observer
      if (this._popupResizeObserver) {
        this._popupResizeObserver.disconnect();
      }

      let lastHeight = popup.getBoundingClientRect().height;
      const ro = new ResizeObserver(() => {
        const h = popup.getBoundingClientRect().height;
        // Only reposition on significant height changes
        if (Math.abs(h - lastHeight) > 2) {
          lastHeight = h;
          this._positionPopup(name);
        }
      });
      ro.observe(popup);
      this._popupResizeObserver = ro;
    },

    /**
     * Returns the currently VISIBLE viewport rect, i.e. excluding the area
     * covered by an open on-screen keyboard. Uses the VisualViewport API
     * (supported on iOS Safari 13+, iOS Chrome and Android Chrome) instead
     * of window.innerWidth/innerHeight, which do not reliably shrink when
     * the keyboard opens on all mobile browsers.
     *
     * offsetTop/offsetLeft matter because WebKit (iOS Safari + iOS Chrome,
     * same engine) keeps `position: fixed` elements anchored to the LAYOUT
     * viewport, not the visible one — so when the visual viewport is offset
     * (keyboard open), a fixed popup positioned with plain 0-based top/left
     * values can end up rendered behind the keyboard even though the math
     * "looks" correct. Adding the offset compensates for that.
     */
    _visibleViewportRect(this: any): {
      width: number;
      height: number;
      offsetTop: number;
      offsetLeft: number;
    } {
      const vv = (window as any).visualViewport;
      if (vv) {
        return {
          width: vv.width,
          height: vv.height,
          offsetTop: vv.offsetTop,
          offsetLeft: vv.offsetLeft,
        };
      }
      return {
        width: window.innerWidth,
        height: window.innerHeight,
        offsetTop: 0,
        offsetLeft: 0,
      };
    },

    /**
     * Polls visualViewport.height every 100ms and calls _positionPopup(name)
     * whenever it actually changed since the last check, stopping once the
     * height has been stable for 3 consecutive checks (~300ms) or a 2s hard
     * cap is hit. Used right after focusing the Lehrer/Thema search input,
     * where fixed-delay retries proved unreliable across browsers (Android
     * Chrome vs. Samsung Internet settle the keyboard/suggestion-bar height
     * at different times) — see call site for the full rationale.
     */
    _watchViewportSettleAndReposition(this: any, name: string) {
      const vv = (window as any).visualViewport;
      const getHeight = () => vv?.height ?? window.innerHeight;

      let lastHeight = getHeight();
      let stableChecks = 0;
      let polls = 0;
      const POLL_MS = 100;
      const STABLE_CHECKS_NEEDED = 3;
      const MAX_POLLS = 20; // 20 * 100ms = 2s hard cap

      const tick = () => {
        polls++;
        const currentHeight = getHeight();
        if (Math.abs(currentHeight - lastHeight) > 1) {
          lastHeight = currentHeight;
          stableChecks = 0;
          this._positionPopup(name);
        } else {
          stableChecks++;
        }
        if (stableChecks < STABLE_CHECKS_NEEDED && polls < MAX_POLLS) {
          setTimeout(tick, POLL_MS);
        }
      };
      setTimeout(tick, POLL_MS);
    },

    /**
     * Positions a filter popup as a speech bubble next to its trigger button.
     * Chooses right/left based on available space; if neither side has enough
     * room for the popup, falls back to a below-button placement (tail top).
     */
    _positionPopup(this: any, name: string) {
      const trigger = document.querySelector<HTMLElement>(
        `[data-popup-trigger="${name}"]`
      );
      const popup = document.querySelector<HTMLElement>(
        `.ymh-popup[data-popup="${name}"]`
      );
      if (!trigger || !popup) return;

      // Reset classes and inline styles before measuring
      popup.classList.remove(
        "ymh-popup--right",
        "ymh-popup--left",
        "ymh-popup--below",
        "is-visible"
      );
      popup.style.top = "";
      popup.style.left = "";
      popup.style.right = "";
      popup.style.maxHeight = "";

      // Use the visible viewport (excludes the on-screen keyboard) so the
      // popup is always sized/placed to fit what the user can actually see.
      const vvRect = this._visibleViewportRect();
      const viewportW = vvRect.width;
      const viewportH = vvRect.height;
      const offsetTop = vvRect.offsetTop;
      const offsetLeft = vvRect.offsetLeft;
      // Constrain popup height up front so getBoundingClientRect reflects the
      // final height. This guarantees the popup never exceeds the viewport.
      const maxAllowedHeight = viewportH - 2 * POPUP_MARGIN;
      popup.style.maxHeight = `${maxAllowedHeight}px`;

      // Temporarily render popup invisible-but-laid-out to measure its size
      popup.style.visibility = "hidden";
      popup.classList.add("is-visible");
      const btnRect = trigger.getBoundingClientRect();
      const popupRect = popup.getBoundingClientRect();
      const popupWidth = Math.min(
        popupRect.width || 240,
        viewportW - 2 * POPUP_MARGIN
      );
      // Trust the MEASURED height directly instead of re-clamping it to
      // `maxAllowedHeight` — the earlier version of this code re-clamped
      // here "just in case", but that meant the "below" placement math
      // further down could end up using a slightly smaller height than
      // what actually got rendered, pushing the popup's real bottom edge a
      // few pixels further down than assumed. Trusting the already-correct
      // CSS-rendered measurement avoids that mismatch entirely.
      const popupHeight = popupRect.height || 200;
      popup.style.visibility = "";

      const isMobile = viewportW < 640;
      const spaceRight = viewportW - btnRect.right;
      const spaceLeft = btnRect.left;
      const fitsRight = spaceRight >= popupWidth + POPUP_GAP + POPUP_MARGIN;
      const fitsLeft = spaceLeft >= popupWidth + POPUP_GAP + POPUP_MARGIN;

      if (isMobile || (!fitsRight && !fitsLeft)) {
        // ── Fallback: place below the button, centred horizontally ─────────
        let left = btnRect.left + btnRect.width / 2 - popupWidth / 2;
        left = Math.max(
          offsetLeft + POPUP_MARGIN,
          Math.min(left, offsetLeft + viewportW - popupWidth - POPUP_MARGIN)
        );
        let top = btnRect.bottom + POPUP_GAP;
        // If not enough space below (e.g. keyboard covers the lower part of
        // the screen), clamp so the popup stays fully within the VISIBLE
        // area — offsetTop/viewportH already exclude the keyboard.
        if (top + popupHeight > offsetTop + viewportH - POPUP_MARGIN) {
          top = Math.max(
            offsetTop + POPUP_MARGIN,
            offsetTop + viewportH - popupHeight - POPUP_MARGIN
          );
        }
        popup.style.left = `${left}px`;
        popup.style.top = `${top}px`;
        popup.classList.add("ymh-popup--below");
        // Tail points at button centre, clamped inside the popup bounds
        let tailLeft = btnRect.left + btnRect.width / 2 - left;
        tailLeft = Math.max(
          POPUP_TAIL_INSET,
          Math.min(tailLeft, popupWidth - POPUP_TAIL_INSET)
        );
        popup.style.setProperty("--ymh-tail-left", `${tailLeft}px`);
      } else {
        // ── Side placement: popup vertically centred on button ─────────────
        const placeRight = fitsRight && (spaceRight >= spaceLeft || !fitsLeft);
        const btnCenterY = btnRect.top + btnRect.height / 2;

        // Preferred top so button-centre is popup-centre
        let top = btnCenterY - popupHeight / 2;

        // Clamp to the VISIBLE viewport (excludes the keyboard): shift
        // up/down if popup would spill.
        if (top < offsetTop + POPUP_MARGIN) {
          top = offsetTop + POPUP_MARGIN;
        } else if (top + popupHeight > offsetTop + viewportH - POPUP_MARGIN) {
          top = offsetTop + viewportH - popupHeight - POPUP_MARGIN;
        }
        popup.style.top = `${top}px`;

        let sideLeft: number;
        if (placeRight) {
          sideLeft = btnRect.right + POPUP_GAP;
          popup.classList.add("ymh-popup--right");
        } else {
          sideLeft = btnRect.left - popupWidth - POPUP_GAP;
          popup.classList.add("ymh-popup--left");
        }
        // Clamp to viewport so the popup can't spill sideways either
        sideLeft = Math.max(
          offsetLeft + POPUP_MARGIN,
          Math.min(sideLeft, offsetLeft + viewportW - popupWidth - POPUP_MARGIN)
        );
        popup.style.left = `${sideLeft}px`;

        // Tail points at the button's vertical centre, clamped inside
        let tailTop = btnCenterY - top;
        tailTop = Math.max(
          POPUP_TAIL_INSET,
          Math.min(tailTop, popupHeight - POPUP_TAIL_INSET)
        );
        popup.style.setProperty("--ymh-tail-top", `${tailTop}px`);
      }

      popup.classList.add("is-visible");
    },
  };
}
